import { useMemo } from 'react';
import { useAssets } from '../../lib/api/assets';
import { AssetCategory } from '../../lib/types';
import { HeatmapPicker, DayDensity } from './HeatmapPicker';

interface Props {
  value: string;
  onPick: (iso: string) => void;
  /** Assets of this category drive the density. */
  category: AssetCategory;
  /** Don't count the asset we're editing in its own density. */
  excludeAssetId?: number;
}

/** Thin wrapper around `HeatmapPicker` — counts existing assets of the
 *  given category per day. Used in the asset modal so Doreen can spot
 *  quiet weeks before assigning a date. */
export const HeatmapDatePicker = ({ value, onPick, category, excludeAssetId }: Props) => {
  const { data: assets = [] } = useAssets({ category });

  const dayItems = useMemo(() => {
    const m = new Map<string, DayDensity>();
    for (const a of assets) {
      if (!a.publish_date) continue;
      if (excludeAssetId && a.id === excludeAssetId) continue;
      const existing = m.get(a.publish_date) ?? { count: 0, labels: [] as string[] };
      existing.count += 1;
      existing.labels?.push(a.title || a.asset_type);
      m.set(a.publish_date, existing);
    }
    return m;
  }, [assets, excludeAssetId]);

  return (
    <HeatmapPicker
      value={value}
      onPick={onPick}
      dayItems={dayItems}
      triggerLabel="See planning"
      triggerTooltip="See the existing planning to spot quiet weeks"
      countNoun="planned"
    />
  );
};

export default HeatmapDatePicker;
